import { ReactNode, useEffect } from 'react';
import { z } from 'zod';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import { AppContext, type AppConfig, type AppContextType, type Theme, type LightVariant, type DarkVariant } from '@/contexts/AppContext';
import i18n from '@/lib/i18n';

interface AppProviderProps {
  children: ReactNode;
  /** Application storage key */
  storageKey: string;
  /** Default app configuration */
  defaultConfig: AppConfig;
  /** Optional list of preset relays to display in the RelaySelector */
  presetRelays?: { name: string; url: string }[];
}

// Zod schema for AppConfig validation
const AppConfigSchema = z.object({
  theme: z.enum(['dark', 'light', 'system']),
  lightVariant: z.enum(['default', 'soft', 'cool']),
  darkVariant: z.enum(['default', 'midnight', 'amoled', 'nord', 'dracula', 'catppuccin']),
  primaryColor: z.string().optional(),
  relayUrl: z.string().url().optional(), // Deprecated, kept for backward compatibility
  relayUrls: z.array(z.string().url()),
  projectTemplate: z.string().url(),
  esmUrl: z.string().url(),
  corsProxy: z.string().url(),
  faviconUrl: z.string().url(),
  previewDomain: z.string().min(1),
  language: z.string(),
  showcaseEnabled: z.boolean(),
  showcaseModerator: z.string(),
  ngitServers: z.array(z.string()),
  systemPromptTemplate: z.string().optional(),
  systemPromptTemplateVanilla: z.string().optional(),
}) satisfies z.ZodType<AppConfig>;

export function AppProvider(props: AppProviderProps) {
  const {
    children,
    storageKey,
    defaultConfig,
    presetRelays,
  } = props;

  // App configuration state with localStorage persistence
  const [rawConfig, setConfig] = useLocalStorage<Partial<AppConfig>>(
    storageKey,
    {},
    {
      serialize: JSON.stringify,
      deserialize: (value: string) => {
        const parsed = JSON.parse(value);
        return AppConfigSchema.partial().parse(parsed);
      }
    }
  );

  // Generic config updater with callback pattern
  const updateConfig = (updater: (currentConfig: Partial<AppConfig>) => Partial<AppConfig>) => {
    setConfig(updater);
  };

  // Migrate from single relayUrl to relayUrls array
  let mergedConfig = { ...defaultConfig, ...rawConfig };
  if (rawConfig.relayUrl && (!rawConfig.relayUrls || rawConfig.relayUrls.length === 0)) {
    mergedConfig.relayUrls = [rawConfig.relayUrl];
    // Remove the deprecated field from storage
    setConfig({ ...rawConfig, relayUrls: [rawConfig.relayUrl], relayUrl: undefined });
  }

  const config = mergedConfig;

  const appContextValue: AppContextType = {
    config,
    updateConfig,
    presetRelays,
  };

  // Apply theme effects to document
  useApplyTheme(config.theme);

  // Apply variant effects to document
  useApplyVariant(config.theme, config.lightVariant, config.darkVariant);

  // Apply custom primary color
  useApplyPrimaryColor(config.primaryColor);

  // Apply language effects to i18n
  useApplyLanguage(config.language);

  return (
    <AppContext.Provider value={appContextValue}>
      {children}
    </AppContext.Provider>
  );
}

/**
 * Hook to apply theme changes to the document root
 */
function useApplyTheme(theme: Theme) {
  useEffect(() => {
    const root = window.document.documentElement;

    root.classList.remove('light', 'dark');

    if (theme === 'system') {
      const systemTheme = window.matchMedia('(prefers-color-scheme: dark)')
        .matches
        ? 'dark'
        : 'light';

      root.classList.add(systemTheme);
      return;
    }

    root.classList.add(theme);
  }, [theme]);

  // Handle system theme changes when theme is set to "system"
  useEffect(() => {
    if (theme !== 'system') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = () => {
      const root = window.document.documentElement;
      root.classList.remove('light', 'dark');

      const systemTheme = mediaQuery.matches ? 'dark' : 'light';
      root.classList.add(systemTheme);
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [theme]);
}

/**
 * Hook to apply variant changes to document based on current theme
 */
function useApplyVariant(theme: Theme, lightVariant: LightVariant, darkVariant: DarkVariant) {
  useEffect(() => {
    const root = window.document.documentElement;

    // Remove all variant classes
    root.classList.remove(
      'variant-default',
      'variant-soft',
      'variant-cool',
      'variant-midnight',
      'variant-amoled',
      'variant-nord',
      'variant-dracula',
      'variant-catppuccin'
    );

    // Determine effective theme (resolve "system")
    const effectiveTheme = theme === 'system'
      ? (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
      : theme;

    // Apply the appropriate variant based on current theme
    const variant = effectiveTheme === 'dark' ? darkVariant : lightVariant;
    root.classList.add(`variant-${variant}`);
  }, [theme, lightVariant, darkVariant]);

  // Handle system theme changes when theme is set to "system"
  useEffect(() => {
    if (theme !== 'system') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = () => {
      const root = window.document.documentElement;

      // Remove all variant classes
      root.classList.remove(
        'variant-default',
        'variant-soft',
        'variant-cool',
        'variant-midnight',
        'variant-amoled',
        'variant-nord',
        'variant-dracula',
        'variant-catppuccin'
      );

      // Apply the appropriate variant based on new system theme
      const effectiveTheme = mediaQuery.matches ? 'dark' : 'light';
      const variant = effectiveTheme === 'dark' ? darkVariant : lightVariant;
      root.classList.add(`variant-${variant}`);
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [theme, lightVariant, darkVariant]);
}

/**
 * Hook to apply custom primary color to document
 */
function useApplyPrimaryColor(primaryColor?: string) {
  useEffect(() => {
    const root = window.document.documentElement;

    if (primaryColor) {
      root.style.setProperty('--primary', primaryColor);
      root.style.setProperty('--ring', primaryColor);
      root.style.setProperty('--sidebar-primary', primaryColor);
      root.style.setProperty('--sidebar-ring', primaryColor);
    } else {
      // Remove custom properties to fallback to CSS defaults
      root.style.removeProperty('--primary');
      root.style.removeProperty('--ring');
      root.style.removeProperty('--sidebar-primary');
      root.style.removeProperty('--sidebar-ring');
    }
  }, [primaryColor]);
}

/**
 * Hook to apply language changes to i18n
 */
function useApplyLanguage(language?: string): void {
  useEffect(() => {
    i18n.changeLanguage(language ?? navigator.language);
  }, [language]);
}
